import React, { useEffect, useState } from 'react';
import PatchCard from './patchCard';
import Styles from './patchStyle.module.scss';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import { LoaderCircle } from 'lucide-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../services/apiService';

export default function PatchCenterDashboard() {
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const categories = ['New', 'In Progress', 'Completed', 'Archived'];

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusColors = {
    New: 'bg-red-600',
    'In Progress': 'bg-gray-500',
    Completed: 'bg-green-500',
    Archived: 'bg-gray-700',
  };
  const statusColors2 = {
    New: 'bg-red-500',
    'In Progress': 'bg-gray-300',
    Completed: 'bg-green-300',
    Archived: 'bg-gray-400',
  };

  const getRemediationList = async () => {
    try {
      if (auth && auth.userData) {
        const response = await api.get(`${Project.api}patchcenterservice/remediationplan/${selectedTenantId}/remediation-plan-list`);

        const { data } = response;
        const apiData = data.data;

        const mapStatusToCategory = (status) => {
          switch (status) {
            case 'Created':
            case 'SubmittedForReview':
            case 'SubmittedForApproval':
            case 'Approved':
              return 'New';
            case 'CommandExecutionInitiated':
            case 'ExecutionCommandReady':
              return 'In Progress';
            case 'Completed':
            case 'CompletedWithErrors':
            case 'NoTaskNeeded':
            case 'Unknown':
              return 'Completed';
            case 'ReviewRejected':
            case 'ApprovalRejected':
            case 'Aborted':
              return 'Archived';
            default:
              return 'New'; // fallback
          }
        };

        const mappedTasks = apiData.flatMap((group) =>
          group.remediationPlans.map((plan) => {
            // Calculate total endpoints
            const totalEndpoints = plan.agentInfo?.reduce((sum, info) => sum + info.agentCount, 0) || 0;

            // Calculate completed endpoints
            const completedEndpoints =
              plan.agentInfo
                ?.filter(
                  (info) =>
                    info.agentStatus === 'Completed' || info.agentStatus === 'CompletedWithErrors' || info.agentStatus === 'NoTaskNeeded',
                )
                .reduce((sum, info) => sum + info.agentCount, 0) || 0;

            // Calculate % progress
            const progress = totalEndpoints > 0 ? Math.round((completedEndpoints / totalEndpoints) * 100) : 0;

            return {
              id: plan.remediationPlanID,
              owner: 'N/A',
              title: plan.planName,
              date: new Date(plan.remediationPlanStartTime).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              }),
              endpoints: totalEndpoints,
              progress: progress,
              status: mapStatusToCategory(group.status),
              originalStatus: group.status,
              prediction: plan.isEmergencyRemediationPlan,
              startTime: plan.remediationPlanStartTime,
            };
          }),
        );

        // Sort tasks within each status category
        mappedTasks.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

        setTasks(mappedTasks);
      }
    } catch (error) {
      console.error('Error fetching remediation list:', error);
    }
  };

  const getRemediationCount = async () => {
    try {
      if (auth && auth.userData) {
        const response = await api.get(`${Project.api}patchcenterservice/remediationplan/${selectedTenantId}/remediation-plan-count`);
        const { data } = response;
        // console.log('Remediation count data:', data);
      }
    } catch (error) {
      console.error('Error fetching remediation count:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([getRemediationList()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <LoaderCircle className="animate-spin w-10 h-10 text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <div className="grid grid-cols-4 gap-4 w-full">
        {categories.map((category) => (
          <div key={category} className="w-full">
            {/* Status Bar */}
            <div className={`${statusColors2[category]} flex items-center w-full rounded-lg overflow-hidden relative`}>
              <div
                className={`${statusColors[category]} text-white text-base font-medium px-6 py-2 flex items-center w-full h-full rounded-lg`}
              >
                {category}
              </div>
              <div className="flex items-center px-10 py-1 rounded-r-2xl relative -ml-8 z-10">
                <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center text-white font-medium text-sm">
                  {tasks.filter((task) => task.status === category).length}
                </div>
              </div>
            </div>

            {/* Patch Cards - Scrollable Section */}
            <div
              className="mt-4 max-h-[950px] pr-2 custom-scrollbar"
              style={{
                overflowY: 'auto',
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE/Edge
              }}
            >
              <div className="grid gap-4">
                {tasks
                  .filter((task) => task.status === category)
                  .map((task) => (
                    <PatchCard key={task.id} task={task} getRemediationList={getRemediationList} />
                  ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
